import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMyInvites, useAcceptOrgInviteById } from "@/hooks/use-orgs";
import {
  useMyTesterInvites,
  useAcceptTesterInviteById,
  acceptTesterInviteErrorKey,
} from "@/hooks/use-my-tester-invites";
import { useAppStore } from "@/store";
import { PageHeader, PageSkeleton, EmptyState, Card, Badge, Button } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/invites")({
  component: InvitesPage,
});

/**
 * Personal invites page: org-membership invites (GET /invites/mine) and
 * tester-authorization invites (GET /tester-invites/mine). Neither
 * response carries a raw token, so both sections accept by invite id —
 * the server-side profile-email match is the authorization gate. The
 * email-link routes remain the primary accept flow.
 */
function InvitesPage() {
  const { t } = useTranslation(["org", "testers"]);
  const resolvedLocale = useAppStore((s) => s.resolvedLocale);

  const { data, isLoading, error } = useMyInvites();
  const testerInvitesQuery = useMyTesterInvites();
  const acceptOrgInvite = useAcceptOrgInviteById();
  const acceptTesterInvite = useAcceptTesterInviteById();

  const invites = data?.invites ?? [];
  const testerInvites = testerInvitesQuery.data?.invites ?? [];

  const handleAcceptOrgInvite = (inviteId: string) => {
    acceptOrgInvite.mutate(inviteId, {
      onSuccess: () => toast.success(t("org:inviteAccepted")),
      onError: (err) =>
        toast.error(
          t(
            err.message.toLowerCase().includes("already")
              ? "org:inviteAlreadyAccepted"
              : "org:inviteAcceptFailed",
          ),
        ),
    });
  };

  const handleAcceptTesterInvite = (inviteId: string) => {
    acceptTesterInvite.mutate(inviteId, {
      onSuccess: (result) =>
        toast.success(t("testers:invite.acceptSuccess", { orgName: result.org_name })),
      onError: (err) => toast.error(t(acceptTesterInviteErrorKey(err))),
    });
  };

  if (isLoading || testerInvitesQuery.isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl">
          <PageSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl">
        <PageHeader title={t("org:myInvites")} />

        {error ? (
          <p className="mt-6 text-danger">
            {t("org:failedToLoadInvites")}: {String(error)}
          </p>
        ) : invites.length === 0 ? (
          <div className="mt-8">
            <EmptyState title={t("org:noInvitesForYou")} />
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {invites.map((invite) => (
              <Card key={invite.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-medium text-on-surface">{invite.org_name}</p>
                    <p className="text-sm text-muted">
                      {t("org:groups")}:{" "}
                      {invite.group_names.length > 0 ? (
                        <span className="inline-flex flex-wrap gap-1">
                          {invite.group_names.map((g) => (
                            <Badge key={g}>{g}</Badge>
                          ))}
                        </span>
                      ) : (
                        t("org:noGroups")
                      )}
                    </p>
                    <p className="text-sm text-muted">
                      {t("org:invitedBy")}: {invite.invited_by_name}
                    </p>
                    <p className="text-sm text-muted">
                      {t("org:expiresAt")}:{" "}
                      {new Date(invite.expires_at).toLocaleDateString(resolvedLocale)}
                    </p>
                  </div>
                  <Button
                    disabled={acceptOrgInvite.isPending}
                    onClick={() => handleAcceptOrgInvite(invite.id)}
                  >
                    {t("org:accept")}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <h2 className="mt-10 text-lg font-semibold text-on-surface">
          {t("testers:myInvites.title")}
        </h2>

        {testerInvitesQuery.error ? (
          <p className="mt-4 text-danger">{t("testers:errors.loadFailed")}</p>
        ) : testerInvites.length === 0 ? (
          <div className="mt-4">
            <EmptyState variant="inline" title={t("testers:myInvites.empty")} />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {testerInvites.map((invite) => (
              <Card key={invite.id}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-medium text-on-surface">{invite.org_name}</p>
                    <p className="text-sm text-muted">
                      {t("testers:myInvites.invitedBy")}: {invite.invited_by_name}
                    </p>
                    <p className="text-sm text-muted">
                      {t("testers:myInvites.expires")}:{" "}
                      {new Date(invite.expires_at).toLocaleDateString(resolvedLocale)}
                    </p>
                  </div>
                  <Button
                    disabled={acceptTesterInvite.isPending}
                    onClick={() => handleAcceptTesterInvite(invite.id)}
                  >
                    {t("testers:myInvites.accept")}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
